import Info from '@mui/icons-material/Info';
import CircularProgress from '@mui/material/CircularProgress';
import Slider from '@mui/material/Slider';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import useTheme from '@mui/material/styles/useTheme';
import { type CSSProperties, useEffect, useState } from 'react';
import { type CustomTheme } from '../../provider/ThemeProvider';
import { getAllFieldFromArray, getFieldFromArray } from '../Texte';
import { checkUndefined, checkUndefinedNullOrEmpty, checkUndefinedOrNull, isLocalServer } from '../Tools';
import { type SelectDataObject } from './Select';
import InputNonEditValue from './helpers/render-non-edit-value';

type SliderDataObject = SelectDataObject<{ tLabel?: string }>;

export type InputSliderProps = {
    autoWidth?: boolean;
    blink?: boolean;
    className?: string;
    data?: SliderDataObject[];
    disabled?: boolean;
    edit?: boolean;
    einheit?: string;
    error?: string;
    fatLabel?: boolean;
    helperText?: string;
    hideLabel?: boolean;
    id?: string;
    info?: string;
    l?: string;
    loading?: boolean;
    max?: number;
    min?: number;
    noFormat?: boolean;
    seperateNegativePositive?: number;
    step?: number;
    testid?: string;
    textAlign?: CSSProperties['textAlign'];
} & (
    | {
          callback?: (params: { id: string; val: SliderDataObject['i'] }) => void;
          type?: 'slider';
          v?: SliderDataObject['i'];
          vNew?: SliderDataObject['i'];
      }
    | {
          callback?: (params: { id: string; val: [SliderDataObject['i'], SliderDataObject['i']] }) => void;
          type?: 'range';
          v?: [SliderDataObject['i'], SliderDataObject['i']];
          vNew?: [SliderDataObject['i'], SliderDataObject['i']];
      }
);

const InputSlider = (moduleProps: InputSliderProps) => {
    const {
        autoWidth,
        blink,
        callback,
        className,
        data,
        disabled,
        edit,
        einheit,
        error,
        fatLabel,
        helperText,
        hideLabel,
        id,
        info,
        l,
        loading,
        max,
        min,
        noFormat,
        seperateNegativePositive,
        step,
        testid,
        textAlign,
        type,
        v,
        vNew,
    } = {
        autoWidth: false,
        blink: false,
        disabled: false,
        edit: false,
        fatLabel: false,
        hideLabel: false,
        loading: false,
        noFormat: false,
        type: 'slider',
        ...moduleProps,
    };
    const theme: CustomTheme = useTheme();
    const [value, setValue] = useState<InputSliderProps['v']>(v);

    const getValue = (val) => {
        const newValue = !checkUndefinedNullOrEmpty(val)
            ? getFieldFromArray(data, 'tLabel', val, 'i', undefined) || getFieldFromArray(data, 't', val, 'i', undefined) || (noFormat && val) || val.toLocaleString()
            : '-';
        return newValue + (einheit && newValue !== 'beliebig' && newValue !== 'Beliebig' && newValue !== '-' ? ' ' + einheit : '');
    };

    /**
     * Handles the Changes of state for the input element
     * @param {Object} e - Event-Object
     * @param {(Number | Array)} value - New State of Input
     */
    const handleChange = (e, val) => {
        if (!checkUndefinedOrNull(seperateNegativePositive)) {
            const minVal = val[0] <= seperateNegativePositive ? val[0] : seperateNegativePositive;
            const maxVal = val[1] >= seperateNegativePositive ? val[1] : seperateNegativePositive;
            setValue([minVal, maxVal]);
        } else {
            setValue(val);
        }
    };

    /**
     * Calls the Callback-Function with a Number (Slider) or an Array (Range) as Callback-Object {id: [id], val: [returnValue]}
     */
    const handleSubmit = () => {
        // @ts-expect-error
        if (callback) callback({ id: id, val: value });
        else console.error('Callback-Funktion fehlt!\nInput-Key: inputRange_' + id);
    };

    const steps = [];
    let showDataWarning = false;
    if (!checkUndefinedNullOrEmpty(data)) {
        data.map((row) => {
            if (!showDataWarning && checkUndefinedOrNull(row.i)) showDataWarning = true;
            if (!checkUndefinedOrNull(row)) {
                steps.push({ value: !checkUndefinedOrNull(row.i) ? row.i : row, label: row.t ? row.t + (einheit && row.t !== 'beliebig' && row.t !== 'Beliebig' ? ' ' + einheit : '') : undefined });
            }
        });
    } else {
        steps.push({ value: 0, label: undefined });
        steps.push({ value: 1, label: undefined });
    }
    if (isLocalServer() && showDataWarning) console.error('Data-Props is not in correct Shape!' + '\nid: inputRange_' + id);

    useEffect(
        function () {
            setValue(!checkUndefined(vNew) ? vNew : v);
        },
        [v, vNew]
    );

    useEffect(
        function () {
            if (blink) {
                setTimeout(() => {
                    const element = document.getElementById(id);
                    if (!checkUndefinedOrNull(element)) {
                        element.scrollIntoView({ block: 'center' });
                    }
                }, 500);
            }
        },
        [blink, id]
    );

    const stepValues = getAllFieldFromArray(steps, 'value', []);

    return edit ? (
        <div
            key={'inputSlider' + (testid ? testid : id) + '_div'}
            data-testid={testid ? testid : id}
            className={[className, !checkUndefinedOrNull(seperateNegativePositive) ? 'zeroLine' : '', blink ? 'blinkInputfield' : '', 'inputSlider'].join(' ')}
            style={{ width: !autoWidth ? '100%' : undefined }}
        >
            <Slider
                key={'inputSlider' + (testid ? testid : id) + '_slider'}
                aria-labelledby={id + '_label'}
                getAriaValueText={getValue}
                valueLabelFormat={getValue}
                id={id}
                valueLabelDisplay={checkUndefinedNullOrEmpty(data) || loading ? 'off' : 'on'}
                disabled={checkUndefinedNullOrEmpty(data) || loading || disabled}
                min={min || Math.min(...stepValues)}
                max={max || Math.max(...stepValues)}
                marks={checkUndefinedOrNull(step) ? steps : true}
                step={step}
                onChange={handleChange}
                onChangeCommitted={handleSubmit}
                // @ts-expect-error
                value={
                    type === 'range'
                        ? [
                              !checkUndefinedOrNull(value) && !checkUndefined(value[0]) ? value[0] : min || Math.min(...stepValues),
                              !checkUndefinedOrNull(value) && !checkUndefined(value[1]) ? value[1] : max || Math.max(...stepValues),
                          ]
                        : !checkUndefined(value)
                          ? value
                          : min || Math.min(...stepValues)
                }
            />
            {(l || error || helperText) && (
                <div key={'inputSlider' + (testid ? testid : id) + '_header'} className="sliderHeading">
                    {l && (
                        <Typography
                            key={'inputSlider' + (testid ? testid : id) + '_label'}
                            data-testid="label"
                            id={id + '_label'}
                            variant="body1"
                            style={{ color: !loading && !checkUndefinedOrNull(error) ? theme?.palette?.error?.main : undefined, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                        >
                            {loading && <CircularProgress key={'inputSlider' + (testid ? testid : id) + '_labelLoading'} color="primary" className="iconMarginRight" size="1.5rem" />}
                            {l}
                            {!checkUndefinedOrNull(info) && (
                                <div
                                    key={'inputSlider' + (testid ? testid : id) + '_labelInfo'}
                                    data-testid="infoIcon"
                                    className="materialIcon"
                                    style={{ color: !loading && !checkUndefinedOrNull(error) ? theme?.palette?.error?.main : undefined, marginLeft: 8 }}
                                >
                                    <Tooltip key={'inputSlider' + (testid ? testid : id) + '_labelInfoTooltip'} title={info}>
                                        <span key={'inputSlider' + (testid ? testid : id) + '_labelInfoSpan'}>
                                            <Info key={'inputSlider' + (testid ? testid : id) + '_labelInfoIcon'} color="primary" />
                                        </span>
                                    </Tooltip>
                                </div>
                            )}
                        </Typography>
                    )}
                    {(!checkUndefinedOrNull(error) || !checkUndefinedOrNull(helperText)) && !loading && (
                        <Typography
                            key={'inputSlider' + (testid ? testid : id) + '_helperText'}
                            id={id + '_helperText'}
                            variant="body2"
                            style={{ fontSize: '0.75rem', textAlign: 'center', color: !checkUndefinedOrNull(error) ? theme?.palette?.error?.main : theme?.palette?.text?.secondary }}
                        >
                            {!checkUndefinedOrNull(error) ? error : helperText}
                        </Typography>
                    )}
                </div>
            )}
        </div>
    ) : (
        <InputNonEditValue
            autoWidth={autoWidth}
            fatLabel={fatLabel}
            finalId={!checkUndefinedNullOrEmpty(testid) ? testid : id}
            hideLabel={hideLabel}
            info={info}
            l={l}
            loading={loading}
            textAlign={textAlign}
            value={
                type === 'range'
                    ? getValue((v && v[0]) || min || Math.min(...stepValues)) + ' bis ' + getValue((v && v[1]) || max || Math.max(...stepValues))
                    : getValue(v || min || Math.min(...stepValues))
            }
        />
    );
};

export default InputSlider;

/**
 * Input Callback
 *
 * @callback inputCallback
 * @param {Object} e - Return-Objekt
 * @param {String} e.id - ID für Callback-Handler
 * @param {Number | Array} e.val - Return Value
 */
